import { z } from "zod";
/**
 * Infrastructure costs — what the platform itself pays its providers (GCP,
 * MongoDB Atlas, the Grok ingest bot, sms-fly, the domain, Google Workspace),
 * for the superadmin «Інфраструктура» screen (owner request 2026-10-14).
 *
 * Everything is entered BY HAND (owner decision 2026-10-14): a registry of
 * services with their expected amount and next due date, plus a history of
 * payments recorded against them. No provider billing API is read.
 *
 * Same wire rule as admin-finance.ts: RAW VALUES ONLY — amounts as numbers in
 * their own currency, dates as `YYYY-MM-DD`, categories as enum keys. USD
 * totals are computed here (`infraToUsd`) so api and admin agree on them; the
 * UI only formats.
 *
 * Dates are calendar dates without a time. All arithmetic is UTC on the
 * `YYYY-MM-DD` string; "today" is the caller's business date (Europe/Kyiv) —
 * this module never reads a clock.
 */
export const INFRA_CURRENCIES = ["USD", "EUR", "CHF", "UAH"];
export const INFRA_CATEGORIES = [
    "hosting",
    "database",
    "sms",
    "domain",
    "email",
    "acquisition",
    "other",
];
export const INFRA_BILLING_KINDS = ["recurring", "prepaid", "one_off"];
export const INFRA_CADENCES = ["monthly", "quarterly", "yearly"];
export const InfraCurrencySchema = z.enum(INFRA_CURRENCIES);
export const InfraCategorySchema = z.enum(INFRA_CATEGORIES);
export const InfraBillingKindSchema = z.enum(INFRA_BILLING_KINDS);
export const InfraCadenceSchema = z.enum(INFRA_CADENCES);
/** A calendar date, `YYYY-MM-DD`. */
const CalendarDateSchema = z.string().date();
/**
 * A billing-console link. The admin renders it as `<a href>`, and `z.string().url()`
 * alone accepts `javascript:` / `data:` URLs (anything `new URL()` parses) — so
 * only `https://` is allowed.
 */
export const InfraBillingUrlSchema = z
    .string()
    .url()
    .refine((url) => url.startsWith("https://"), { message: "must be an https:// URL" });
/** Default lead time of the «нагадати за N днів» reminder. */
export const DEFAULT_REMIND_DAYS_BEFORE = 3;
const InfraServiceFieldsSchema = z.object({
    name: z.string().trim().min(1).max(80),
    provider: z.string().trim().max(80).nullish(),
    category: InfraCategorySchema,
    purpose: z.string().trim().max(200).nullish(),
    billingUrl: InfraBillingUrlSchema.nullish(),
    notes: z.string().max(1000).nullish(),
    billingKind: InfraBillingKindSchema,
    /** Required for `recurring`; optional for `prepaid` (a top-up on a schedule); ignored for `one_off`. */
    cadence: InfraCadenceSchema.nullish(),
    /** The expected amount of ONE payment, in `currency`. */
    amount: z.number().nonnegative(),
    currency: InfraCurrencySchema,
    /** Usage-billed services (GCP, Atlas) only have an estimate until the invoice arrives. */
    amountIsEstimate: z.boolean(),
    /** `null` = not scheduled: the service stays out of the schedule and gets no reminder. */
    nextDueDate: CalendarDateSchema.nullish(),
    remindDaysBefore: z.number().int().min(0).max(60),
    active: z.boolean(),
});
export const InfraServiceSchema = InfraServiceFieldsSchema.extend({
    id: z.string().min(1),
    /**
     * Read side: a value stored before the https-only rule (or written around the
     * API) degrades to "no link" instead of failing the whole registry parse.
     */
    billingUrl: InfraBillingUrlSchema.nullish().catch(null),
    /**
     * The day of month the schedule is anchored to (1–31), so a payment due on
     * the 31st lands on the last day of a shorter month and returns to the 31st
     * after it, instead of drifting to the 28th for good.
     */
    dueDayOfMonth: z.number().int().min(1).max(31).nullish(),
    /** Set only on the default services `seed-defaults` creates; unique. */
    seedKey: z.string().nullish(),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
});
/** A recurring service needs a cadence and a date, or it can never be scheduled. */
function recurringIssues(value) {
    if (value.billingKind !== "recurring")
        return [];
    const issues = [];
    if (!value.cadence) {
        issues.push({ code: z.ZodIssueCode.custom, path: ["cadence"], message: "required for recurring" });
    }
    if (!value.nextDueDate) {
        issues.push({ code: z.ZodIssueCode.custom, path: ["nextDueDate"], message: "required for recurring" });
    }
    return issues;
}
function refineRecurring(value, ctx) {
    for (const issue of recurringIssues(value))
        ctx.addIssue(issue);
}
/** Body of `POST services`. Defaults: remind 3 days before, active, not an estimate. */
export const InfraServiceInputSchema = InfraServiceFieldsSchema.extend({
    amountIsEstimate: z.boolean().default(false),
    remindDaysBefore: z.number().int().min(0).max(60).default(DEFAULT_REMIND_DAYS_BEFORE),
    active: z.boolean().default(true),
}).superRefine(refineRecurring);
/**
 * Body of `PATCH services/:id`. No cross-field rule here: the server merges
 * the patch into the stored document and re-checks the result with
 * {@link refineInfraServiceMerged} — a seed service without a date must stay
 * editable (its amount, its notes) before its date is known.
 */
export const InfraServicePatchSchema = InfraServiceFieldsSchema.partial();
/**
 * The recurring rule, for a merged (stored + patch) service: the paths that
 * are missing, empty when valid.
 */
export function refineInfraServiceMerged(merged) {
    return recurringIssues(merged).map((issue) => ({
        path: (issue.path ?? []).map(String),
        message: issue.message ?? "invalid",
    }));
}
export const InfraPaymentSchema = z.object({
    id: z.string().min(1),
    serviceId: z.string().min(1),
    /** Denormalised: the history still reads after a rename or an archive. */
    serviceName: z.string().min(1),
    /** The `nextDueDate` the payment settled; null for an unscheduled payment. */
    dueDate: CalendarDateSchema.nullish(),
    paidAt: CalendarDateSchema,
    amount: z.number().nonnegative(),
    currency: InfraCurrencySchema,
    note: z.string().max(500).nullish(),
    /** User id of the superadmin who recorded it. */
    recordedBy: z.string().min(1),
    /** The `Idempotency-Key` it was recorded under — a repeat returns this payment. */
    idempotencyKey: z.string().nullish(),
    createdAt: z.string().datetime(),
});
/** Body of `POST services/:id/payments`. */
export const RecordInfraPaymentInputSchema = z.object({
    paidAt: CalendarDateSchema,
    amount: z.number().nonnegative(),
    currency: InfraCurrencySchema,
    note: z.string().max(500).nullish(),
});
export const INFRA_SCHEDULE_STATUSES = ["overdue", "due_soon", "upcoming"];
export const InfraScheduleStatusSchema = z.enum(INFRA_SCHEDULE_STATUSES);
export const InfraScheduleItemSchema = z.object({
    serviceId: z.string().min(1),
    serviceName: z.string().min(1),
    dueDate: CalendarDateSchema,
    amount: z.number().nonnegative(),
    currency: InfraCurrencySchema,
    amountUsd: z.number().nonnegative(),
    amountIsEstimate: z.boolean(),
    status: InfraScheduleStatusSchema,
    /** Days from `from` to `dueDate`; negative when overdue. */
    daysUntil: z.number().int(),
});
export const InfraCostsSummarySchema = z.object({
    /** The business date the summary was computed for. */
    today: CalendarDateSchema,
    thisMonthUsd: z.number().nonnegative(),
    next30DaysUsd: z.number().nonnegative(),
    overdue: z.object({ count: z.number().int().nonnegative(), usd: z.number().nonnegative() }),
    next12MonthsUsd: z.number().nonnegative(),
    /**
     * Recorded payments with `paidAt` in the month of `today` — money already
     * spent, exact (a fact, never an estimate), in USD at the rule set's rates.
     */
    paidThisMonthUsd: z.number().nonnegative(),
    /**
     * Recorded payments with `paidAt` in the last {@link SUMMARY_HORIZON_MONTHS}
     * calendar months including the current one: from {@link infraPaidWindowStart}
     * to `today`.
     */
    paidLast12MonthsUsd: z.number().nonnegative(),
    /** True when any amount in the totals is an estimate — the UI prints «≈». */
    hasEstimates: z.boolean(),
    months: z.array(z.object({
        /** `YYYY-MM`. */
        month: z.string().regex(/^\d{4}-\d{2}$/),
        totalUsd: z.number().nonnegative(),
        byService: z.array(z.object({ serviceId: z.string().min(1), serviceName: z.string().min(1), usd: z.number().nonnegative() })),
    })),
    /** Overdue items plus everything due in the next {@link UPCOMING_WINDOW_DAYS} days. */
    upcoming: z.array(InfraScheduleItemSchema),
    /** The rule-set version whose `fx` produced every USD figure. */
    fx: z.object({ asOf: z.string() }),
});
/** How far ahead the «Найближчі платежі» list looks. */
export const UPCOMING_WINDOW_DAYS = 60;
/** The schedule horizon the summary covers. */
export const SUMMARY_HORIZON_MONTHS = 12;
// ── Date arithmetic (UTC, `YYYY-MM-DD`) ──────────────────────────────────────
const DAY_MS = 86_400_000;
function parseDate(date) {
    const [year, month, day] = date.split("-").map(Number);
    return { year, month, day };
}
function formatDate(year, month, day) {
    return `${String(year).padStart(4, "0")}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
}
function daysInMonth(year, month) {
    return new Date(Date.UTC(year, month, 0)).getUTCDate();
}
/** Whole days from `from` to `to` (negative when `to` is earlier). */
export function infraDaysBetween(from, to) {
    const a = parseDate(from);
    const b = parseDate(to);
    return Math.round((Date.UTC(b.year, b.month - 1, b.day) - Date.UTC(a.year, a.month - 1, a.day)) / DAY_MS);
}
/** `date` plus `days` calendar days. */
export function infraAddDays(date, days) {
    const { year, month, day } = parseDate(date);
    const next = new Date(Date.UTC(year, month - 1, day + days));
    return formatDate(next.getUTCFullYear(), next.getUTCMonth() + 1, next.getUTCDate());
}
const CADENCE_MONTHS = { monthly: 1, quarterly: 3, yearly: 12 };
/**
 * The due date one period after `date`, on `anchorDay` — or the month's last
 * day when the month is shorter: 31.01 → 28.02 (29.02 in a leap year) → 31.03.
 */
export function advanceDueDate(date, cadence, anchorDay) {
    const { year, month } = parseDate(date);
    const monthIndex = month - 1 + CADENCE_MONTHS[cadence];
    const nextYear = year + Math.floor(monthIndex / 12);
    const nextMonth = (monthIndex % 12) + 1;
    return formatDate(nextYear, nextMonth, Math.min(anchorDay, daysInMonth(nextYear, nextMonth)));
}
/** The anchor day of a service: its stored `dueDayOfMonth`, else the day of its `nextDueDate`. */
function anchorDayOf(service) {
    if (service.dueDayOfMonth)
        return service.dueDayOfMonth;
    return service.nextDueDate ? parseDate(service.nextDueDate).day : 1;
}
// ── Currency ─────────────────────────────────────────────────────────────────
/**
 * An amount in USD at the rule set's own rates — the same fixed rates every
 * other money surface uses, so the figure is an estimate and is labelled so.
 */
export function infraToUsd(amount, currency, rules) {
    const { eurToUsd, uahPerEur } = rules.fx;
    switch (currency) {
        case "USD":
            return amount;
        case "EUR":
            return amount * eurToUsd;
        case "CHF":
            return amount * rules.swissExport.chfToEurRate * eurToUsd;
        case "UAH":
            return uahPerEur > 0 ? (amount / uahPerEur) * eurToUsd : 0;
    }
}
function statusOf(daysUntil, remindDaysBefore) {
    if (daysUntil < 0)
        return "overdue";
    if (daysUntil <= remindDaysBefore)
        return "due_soon";
    return "upcoming";
}
/**
 * Every payment the active, scheduled services fall due for in
 * `[from, from + months)`, plus each one already overdue (once — the unpaid
 * `nextDueDate` itself, not a backfill of missed periods).
 *
 * A recurring service — and a prepaid one with a cadence — repeats on its
 * anchor day; a prepaid service without a cadence and a one-off payment occur
 * once. Inactive services and services without a `nextDueDate` are left out.
 */
export function projectSchedule(services, options, rules) {
    const { from, months } = options;
    const { year, month } = parseDate(from);
    const endMonthIndex = month - 1 + months;
    const horizonEnd = formatDate(year + Math.floor(endMonthIndex / 12), (endMonthIndex % 12) + 1, 1);
    const items = [];
    for (const service of services) {
        if (!service.active || !service.nextDueDate)
            continue;
        const repeats = service.billingKind !== "one_off" && !!service.cadence;
        const anchorDay = anchorDayOf(service);
        const amountUsd = infraToUsd(service.amount, service.currency, rules);
        let dueDate = service.nextDueDate;
        while (dueDate < horizonEnd) {
            const daysUntil = infraDaysBetween(from, dueDate);
            items.push({
                serviceId: service.id,
                serviceName: service.name,
                dueDate,
                amount: service.amount,
                currency: service.currency,
                amountUsd,
                amountIsEstimate: service.amountIsEstimate,
                status: statusOf(daysUntil, service.remindDaysBefore),
                daysUntil,
            });
            if (!repeats || !service.cadence)
                break;
            // An overdue date is listed once; repeats resume from the first period on or after `from`.
            dueDate = advanceDueDate(dueDate, service.cadence, anchorDay);
            while (dueDate < from)
                dueDate = advanceDueDate(dueDate, service.cadence, anchorDay);
        }
    }
    return items.sort((a, b) => a.dueDate.localeCompare(b.dueDate) || a.serviceName.localeCompare(b.serviceName));
}
/**
 * The first day of the paid-totals window: the 1st of `today`'s month minus
 * `SUMMARY_HORIZON_MONTHS - 1` months, so the window covers 12 calendar months
 * including the current one (2026-10-27 → 2025-11-01).
 */
export function infraPaidWindowStart(today) {
    const { year, month } = parseDate(today);
    const index = year * 12 + (month - 1) - (SUMMARY_HORIZON_MONTHS - 1);
    return formatDate(Math.floor(index / 12), (index % 12) + 1, 1);
}
/**
 * The superadmin summary for `today`: KPIs, the 12-month chart and the
 * «Найближчі платежі» list, all in USD at the rule set's rates. The api calls
 * this; nothing else computes these figures.
 */
export function buildInfraSummary(services, rules, today, payments) {
    const schedule = projectSchedule(services, { from: today, months: SUMMARY_HORIZON_MONTHS }, rules);
    const currentMonth = today.slice(0, 7);
    const sum = (items) => items.reduce((total, item) => total + item.amountUsd, 0);
    const overdueItems = schedule.filter((item) => item.status === "overdue");
    // Paid totals: the caller may pass a wider history; the window is enforced here.
    const paidWindowStart = infraPaidWindowStart(today);
    const paidInWindow = payments.filter((payment) => payment.paidAt >= paidWindowStart && payment.paidAt <= today);
    const sumPaid = (items) => items.reduce((total, payment) => total + infraToUsd(payment.amount, payment.currency, rules), 0);
    const { year, month } = parseDate(today);
    const months = Array.from({ length: SUMMARY_HORIZON_MONTHS }, (_unused, offset) => {
        const index = month - 1 + offset;
        const key = `${year + Math.floor(index / 12)}-${String((index % 12) + 1).padStart(2, "0")}`;
        const inMonth = schedule.filter((item) => 
        // Overdue payments are still owed: they count in the current month's bar.
        offset === 0 ? item.dueDate.slice(0, 7) <= key : item.dueDate.slice(0, 7) === key);
        const byServiceMap = new Map();
        for (const item of inMonth) {
            const entry = byServiceMap.get(item.serviceId) ?? {
                serviceId: item.serviceId,
                serviceName: item.serviceName,
                usd: 0,
            };
            entry.usd += item.amountUsd;
            byServiceMap.set(item.serviceId, entry);
        }
        return { month: key, totalUsd: sum(inMonth), byService: [...byServiceMap.values()] };
    });
    return {
        today,
        thisMonthUsd: sum(schedule.filter((item) => item.dueDate.slice(0, 7) <= currentMonth)),
        next30DaysUsd: sum(schedule.filter((item) => item.daysUntil >= 0 && item.daysUntil <= 30)),
        overdue: { count: overdueItems.length, usd: sum(overdueItems) },
        next12MonthsUsd: sum(schedule),
        paidThisMonthUsd: sumPaid(paidInWindow.filter((payment) => payment.paidAt.slice(0, 7) === currentMonth)),
        paidLast12MonthsUsd: sumPaid(paidInWindow),
        hasEstimates: schedule.some((item) => item.amountIsEstimate),
        months,
        upcoming: schedule.filter((item) => item.daysUntil <= UPCOMING_WINDOW_DAYS),
        fx: { asOf: rules.customs.version },
    };
}
//# sourceMappingURL=infra-costs.js.map